import type { DocumentNode } from 'graphql';
import type { PropsWithChildren, ReactElement } from 'react';

interface LivePreviewProviderProps {
    locale: string;
    space?: string;
    environment?: string;
    debugMode?: boolean;
    enableInspectorMode?: boolean;
    enableLiveUpdates?: boolean;
    targetOrigin?: string | string[];
    experimental?: Record<string, unknown>;
}

interface LivePreviewUpdateOptions {
    locale?: string;
    query?: DocumentNode;
    skip?: boolean;
}

interface InspectorSharedProps {
    entryId?: string;
    assetId?: string;
    locale?: string;
    space?: string;
    environment?: string;
}

interface InspectorModeProps extends InspectorSharedProps {
    fieldId: string;
}

interface UseContentfulLiveUpdates {
    <T>(data: T, options?: LivePreviewUpdateOptions): T;
    <T>(data: T, locale?: string, skip?: boolean): T;
}

interface LivePreviewReactModule {
    ContentfulLivePreviewProvider: (
        props: PropsWithChildren<LivePreviewProviderProps>
    ) => ReactElement;
    useContentfulInspectorMode: (
        sharedProps?: InspectorSharedProps
    ) => (props: InspectorModeProps) => Record<string, string> | null;
    useContentfulLiveUpdates: UseContentfulLiveUpdates;
}

// CJS bridge for @contentful/live-preview/react. The package has a require export,
// but TypeScript's Node16 module checks reject a static import from this CJS file.
const livePreviewReact =
    // eslint-disable-next-line import/no-commonjs
    require('@contentful/live-preview/react') as LivePreviewReactModule;

export const ContentfulLivePreviewProvider =
    livePreviewReact.ContentfulLivePreviewProvider;
export const useContentfulInspectorMode =
    livePreviewReact.useContentfulInspectorMode;
export const useContentfulLiveUpdates =
    livePreviewReact.useContentfulLiveUpdates;
